import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Users, CheckCircle2, ArrowRight, Target, Shield, Clock, Globe } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../../components/SEO';

const RECRUITMENT_IMAGE = 'https://images.unsplash.com/photo-1758520144427-ddb02ac74e9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA8Mzl8MHwxfHNlYXJjaHwxfHxqb2IlMjBpbnRlcnZpZXclMjByZWNydWl0bWVudCUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzQ1Mjk3MTl8MA&ixlib=rb-4.1.0&q=85';

export const RecruitmentServicePage = () => {
  const { t, direction, language } = useLanguage();

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'التوظيف' : 'Recruitment Services' }
  ]);

  const processSteps = language === 'ar' ? [
    {
      number: '01',
      title: 'تحليل المتطلبات',
      description: 'نعمل معك عن كثب لفهم احتياجاتك المحددة وثقافة الشركة وملف المرشح المثالي.',
    },
    {
      number: '02',
      title: 'البحث عن الكفاءات',
      description: 'يستفيد فريقنا من شبكتنا الواسعة وقواعد البيانات لتحديد المرشحين المؤهلين.',
    },
    {
      number: '03',
      title: 'الفحص والتقييم',
      description: 'فحص دقيق يشمل المقابلات والتقييمات والتحقق من المراجع.',
    },
    {
      number: '04',
      title: 'العرض والاختيار',
      description: 'نقدم المرشحين المختارين مع ملفات تعريف مفصلة لمراجعتك.',
    },
    {
      number: '05',
      title: 'العرض والتأهيل',
      description: 'الدعم خلال التفاوض على العرض وعملية التأهيل السلسة.',
    },
  ] : [
    {
      number: '01',
      title: 'Requirements Analysis',
      description: 'We work closely with you to understand your specific needs, company culture, and ideal candidate profile.',
    },
    {
      number: '02',
      title: 'Talent Sourcing',
      description: 'Our team leverages our extensive network and databases to identify qualified candidates.',
    },
    {
      number: '03',
      title: 'Screening & Assessment',
      description: 'Rigorous screening including interviews, assessments, and reference checks.',
    },
    {
      number: '04',
      title: 'Presentation & Selection',
      description: 'We present shortlisted candidates with detailed profiles for your review.',
    },
    {
      number: '05',
      title: 'Offer & Onboarding',
      description: 'Support through offer negotiation and smooth onboarding process.',
    },
  ];

  const benefits = language === 'ar' ? [
    'الوصول إلى شبكة واسعة من الكفاءات في أوروبا والشرق الأوسط',
    'مسؤولو توظيف متخصصون بمعرفة عميقة بالمجال',
    'عمليات فحص وتقييم صارمة',
    'توفير الوقت والتكلفة لفريق الموارد البشرية لديك',
    'قدرات البحث التنفيذي السرية',
    'ضمان الاستبدال لجميع التعيينات',
  ] : [
    'Access to extensive talent network across Europe and Middle East',
    'Industry-specialized recruiters with deep domain knowledge',
    'Rigorous screening and assessment processes',
    'Time and cost savings for your HR team',
    'Confidential executive search capabilities',
    'Replacement guarantee for all placements',
  ];

  const whoItIsFor = language === 'ar' ? [
    'الشركات التي تتوسع في أسواق جديدة',
    'المؤسسات التي لديها وظائف صعبة الملء',
    'الشركات التي تبحث عن كفاءات متخصصة',
    'الشركات الناشئة التي تبني فرقها الأساسية',
    'المؤسسات التي تحتاج إلى توظيف بأعداد كبيرة',
  ] : [
    'Companies expanding into new markets',
    'Organizations with hard-to-fill positions',
    'Businesses seeking specialized talent',
    'Startups building their core teams',
    'Enterprises requiring volume hiring',
  ];

  const featureCards = language === 'ar' ? [
    { icon: Target, title: 'بحث موجه', description: 'مطابقة دقيقة للأدوار المتخصصة' },
    { icon: Shield, title: 'جودة مضمونة', description: 'عملية فحص صارمة' },
    { icon: Clock, title: 'سرعة التنفيذ', description: 'جداول توظيف فعالة' },
    { icon: Globe, title: 'انتشار عالمي', description: 'الوصول إلى الكفاءات الدولية' },
  ] : [
    { icon: Target, title: 'Targeted Search', description: 'Precision matching for specialized roles' },
    { icon: Shield, title: 'Quality Assured', description: 'Rigorous vetting process' },
    { icon: Clock, title: 'Fast Turnaround', description: 'Efficient hiring timelines' },
    { icon: Globe, title: 'Global Reach', description: 'International talent access' },
  ];

  // Section titles and descriptions
  const content = {
    benefitsTitle: language === 'ar' ? 'لماذا تختار خدمات التوظيف لدينا؟' : 'Why Choose Our Recruitment Services?',
    benefitsDescription: language === 'ar' 
      ? 'نجمع بين الخبرة والتكنولوجيا والنهج الشخصي للعثور على التطابق المثالي لمؤسستك.'
      : 'We bring together expertise, technology, and a personalized approach to find the perfect match for your organization.',
    processTitle: language === 'ar' ? 'عملية التوظيف لدينا' : 'Our Recruitment Process',
    processDescription: language === 'ar' ? 'نهج منظم يحقق النتائج' : 'A structured approach that delivers results',
    whoItIsForTitle: language === 'ar' ? 'لمن هذه الخدمة؟' : 'Who Is This For?',
    ctaTitle: language === 'ar' ? 'هل أنت مستعد للعثور على نجمك القادم؟' : 'Ready to Find Your Next Star?',
    ctaDescription: language === 'ar' 
      ? 'دعنا نساعدك في بناء فريق يدفع عملك إلى الأمام.'
      : 'Let us help you build a team that drives your business forward.',
  };

  return (
    <div className="min-h-screen" data-testid="recruitment-service-page">
      <SEO
        title="Recruitment Services - Find the Right Talent"
        titleAr="خدمات التوظيف - اعثر على الكفاءات المناسبة"
        description="WeDo HR recruitment services help organizations find qualified candidates across Europe, Middle East, GCC, and Africa. Executive search, headhunting, and staffing solutions."
        descriptionAr="تساعد خدمات التوظيف من ويدو المؤسسات في العثور على مرشحين مؤهلين في أوروبا والشرق الأوسط ودول الخليج وأفريقيا. البحث التنفيذي واستقطاب الكفاءات وحلول التوظيف."
        keywords="recruitment services, headhunting, executive search, talent acquisition, staffing agency, توظيف, استقطاب كفاءات, بحث تنفيذي"
        canonicalUrl="/services/recruitment"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Hero Section */}
      <section className="bg-white border-b border-[#E5E7EB] py-20">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mb-6">
                {t('services.recruitment.title')}
              </h1>
              <p className="text-xl text-[#6B7280] mb-8">
                {t('services.recruitment.description')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/recruitment-request">
                  <Button size="lg" className="btn-primary" data-testid="request-support-btn">
                    {t('services.recruitment.cta')}
                    <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
                  </Button>
                </Link>
                <Link to="/jobs">
                  <Button size="lg" variant="outline" className="btn-secondary">
                    {t('nav.jobs')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img 
                src={RECRUITMENT_IMAGE} 
                alt="Recruitment services" 
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111827] mb-6">
                {content.benefitsTitle}
              </h2>
              <p className="text-lg text-[#6B7280] mb-8">
                {content.benefitsDescription}
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3" data-testid={`benefit-${index}`}>
                    <CheckCircle2 className="h-5 w-5 text-[#33AADD] flex-shrink-0 mt-0.5" />
                    <span className="text-[#6B7280]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {featureCards.map((card, index) => (
                <Card key={index} className="card-hover">
                  <CardContent className="p-6 text-center">
                    <card.icon className="h-10 w-10 text-[#33AADD] mx-auto mb-4" />
                    <h3 className="font-semibold text-[#111827] mb-2">{card.title}</h3>
                    <p className="text-sm text-[#6B7280]">{card.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-4">
              {content.processTitle}
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              {content.processDescription}
            </p>
          </div>
          <div className="space-y-6">
            {processSteps.map((step, index) => (
              <div 
                key={index} 
                className="flex gap-6 items-start p-6 bg-white rounded-xl border border-[#E5E7EB] hover:shadow-md transition-shadow"
                data-testid={`process-step-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-[#33AADD]">{step.number}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-[#111827] mb-2">{step.title}</h3>
                  <p className="text-[#6B7280]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="section-padding bg-[#F9FAFB]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-[#111827] mb-6 text-center">
              {content.whoItIsForTitle}
            </h2>
            <ul className="space-y-4">
              {whoItIsFor.map((item, index) => (
                <li key={index} className="flex items-center gap-3 p-4 bg-white rounded-lg border border-[#E5E7EB]">
                  <Users className="h-5 w-5 text-[#33AADD]" />
                  <span className="text-[#6B7280]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#33AADD]">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {content.ctaTitle}
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            {content.ctaDescription}
          </p>
          <Link to="/recruitment-request">
            <Button size="lg" variant="secondary" className="bg-white text-[#33AADD] hover:bg-white/90" data-testid="cta-btn">
              {t('services.recruitment.cta')}
              <ArrowRight className={`ms-2 h-5 w-5 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RecruitmentServicePage;
